<template>
    <card>
        <div class="d-flex justify-content-between align-items-center mb-3">
            <h4 class="card-subtitle mb-0">Subscriptions</h4>
            <b-form-checkbox 
                v-model="showInactive" 
                size="sm"
            >
                Show Cancelled/Inactive ({{ inactiveCount }})
            </b-form-checkbox>
        </div>

        <div v-if="loading" class="text-center py-3">
            <b-spinner variant="primary" />
            <p class="mt-2 mb-0">Loading subscriptions...</p>
        </div>

        <div v-else-if="filteredSubscriptions.length === 0" class="text-muted text-center py-3">
            <i class="fas fa-credit-card fa-2x mb-2"></i>
            <p class="mb-0">No {{ showInactive ? '' : 'active ' }}subscriptions found</p>
        </div>

        <div v-else>
            <div v-for="subscription in filteredSubscriptions" 
                 :key="subscription.id" 
                 class="subscription-item border rounded p-3 mb-2"
                 :class="getSubscriptionClasses(subscription)">
                
                <div class="d-flex justify-content-between align-items-start">
                    <div class="flex-grow-1">
                        <h6 class="mb-1 font-weight-bold">{{ subscription.name }}</h6>
                        <div class="subscription-details">
                            <div class="row">
                                <div class="col-sm-6">
                                    <small class="text-muted d-block">
                                        <i class="fas fa-fw fa-calendar-alt mr-1"></i>
                                        Started: {{ formatDate(subscription.startDate) }}
                                    </small>
                                    <small class="text-muted d-block">
                                        <i class="fas fa-fw fa-dollar-sign mr-1"></i>
                                        Monthly: {{ subscription.formattedMonthlyAmount }}
                                    </small>
                                </div>
                                <div class="col-sm-6">
                                    <small v-if="subscription.nextDueDate && subscription.isActive && !subscription.cancelAtPeriodEnd" class="text-muted d-block">
                                        <i class="fas fa-fw fa-clock mr-1"></i>
                                        Next Due: {{ formatDate(subscription.nextDueDate) }}
                                    </small>
                                    <small v-if="subscription.cancelAtPeriodEnd && subscription.isActive" class="text-danger d-block">
                                        <i class="fas fa-fw fa-exclamation-triangle mr-1"></i>
                                        Cancels: {{ formatDate(subscription.nextDueDate) }}
                                    </small>
                                    <small v-if="!subscription.isActive && (subscription.endDate || subscription.canceledAt)" class="text-muted d-block">
                                        <i class="fas fa-fw fa-calendar-times mr-1"></i>
                                        Ended: {{ formatDate(subscription.endDate || subscription.canceledAt) }}
                                    </small>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div class="subscription-status ml-3">
                        <b-badge 
                            :variant="getStatusVariant(subscription.status)"
                            class="status-badge"
                        >
                            {{ subscription.statusDisplayName }}
                        </b-badge>
                        
                        <!-- Action icons below status -->
                        <div class="action-icons mt-2">
                            <b-button 
                                size="sm" 
                                variant="link"
                                class="p-1 action-icon"
                                @click="viewSubscriptionDetails(subscription)"
                                v-b-tooltip.hover="'View Details'"
                            >
                                <i class="fas fa-eye"></i>
                            </b-button>
                            <b-button 
                                v-if="subscription.isActive"
                                size="sm" 
                                variant="link"
                                class="p-1 action-icon text-danger"
                                @click="cancelSubscription(subscription)"
                                v-b-tooltip.hover="'Cancel Subscription'"
                            >
                                <i class="fas fa-times"></i>
                            </b-button>
                        </div>
                    </div>
                </div>

            </div>
        </div>

        <!-- Add Subscription Button (styled like Add Eligible Programs) -->
        <b-button 
            v-if="!hasActiveSubscription" 
            block 
            variant="success" 
            @click="showAddSubscriptionModal"
            class="mt-3"
        >
            <i class="fas fa-plus" />
            Add Subscription
        </b-button>

        <!-- Subscription Details Modal -->
        <b-modal 
            id="subscriptionDetailsModal" 
            ref="subscriptionDetailsModal"
            title="Subscription Details" 
            size="lg"
            @hidden="resetDetailsModal"
        >
            <div v-if="selectedSubscription">
                <b-row>
                    <b-col cols="6">
                        <strong>Subscription Name:</strong>
                        <p>{{ selectedSubscription.name }}</p>
                    </b-col>
                    <b-col cols="6">
                        <strong>Status:</strong>
                        <p>
                            <b-badge :variant="getStatusVariant(selectedSubscription.status)">
                                {{ selectedSubscription.statusDisplayName }}
                            </b-badge>
                        </p>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col cols="6">
                        <strong>Start Date:</strong>
                        <p>{{ formatDateTime(selectedSubscription.startDate) }}</p>
                    </b-col>
                    <b-col cols="6">
                        <strong>Monthly Amount:</strong>
                        <p>{{ selectedSubscription.formattedMonthlyAmount }}</p>
                    </b-col>
                </b-row>
                <b-row v-if="selectedSubscription.nextDueDate">
                    <b-col cols="6">
                        <strong>Next Due Date:</strong>
                        <p>{{ formatDateTime(selectedSubscription.nextDueDate) }}</p>
                    </b-col>
                    <b-col cols="6" v-if="selectedSubscription.endDate || selectedSubscription.canceledAt">
                        <strong>End Date:</strong>
                        <p>{{ formatDateTime(selectedSubscription.endDate || selectedSubscription.canceledAt) }}</p>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col cols="12">
                        <strong>Stripe Subscription ID:</strong>
                        <p class="font-monospace">{{ selectedSubscription.id }}</p>
                    </b-col>
                </b-row>
                
                <!-- Payment Method Section -->
                <hr class="my-4">
                <div class="mb-3">
                    <div class="d-flex justify-content-between align-items-center mb-2">
                        <h6 class="mb-0">Payment Method</h6>
                        <b-button 
                            size="sm" 
                            variant="outline-secondary"
                            @click="toggleChangePaymentMethod"
                            v-if="selectedSubscription.isActive"
                        >
                            <i class="fas fa-edit mr-1"></i>
                            Change
                        </b-button>
                    </div>
                    
                    <div v-if="currentPaymentMethod" class="payment-method-display p-2 border rounded">
                        <i class="fas fa-credit-card mr-2"></i>
                        {{ currentPaymentMethod.brand?.toUpperCase() }} ending in {{ currentPaymentMethod.cardNumber }}
                        <span class="text-muted ml-2">{{ formatCardExpiration(currentPaymentMethod.expMonth, currentPaymentMethod.expYear) }}</span>
                    </div>
                    
                    <div v-else class="text-muted">
                        Payment method information not available
                    </div>
                </div>

                <!-- Change Payment Method Form -->
                <b-collapse v-model="showChangePaymentMethod" class="mb-3">
                    <b-card>
                        <h6>Select New Payment Method</h6>
                        
                        <div v-if="loadingCardsForDetails" class="text-center py-2">
                            <b-spinner size="sm" />
                            <span class="ml-2">Loading cards...</span>
                        </div>
                        
                        <div v-else-if="availableCardsForDetails.length > 0" class="mb-3">
                            <div v-for="card in availableCardsForDetails" :key="card.id" class="mb-2">
                                <div class="card-selection" @click="selectedCardForDetails = card.id">
                                    <div class="d-flex justify-content-between align-items-center">
                                        <div class="d-flex align-items-center">
                                            <b-form-radio 
                                                v-model="selectedCardForDetails" 
                                                :value="card.id"
                                                class="mr-2"
                                            />
                                            <span>
                                                <i class="fas fa-credit-card mr-2"></i>
                                                {{ card.brand?.toUpperCase() }} ending in {{ card.cardNumber }}
                                                <span class="text-muted ml-2">{{ formatCardExpiration(card.expMonth, card.expYear) }}</span>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <div class="d-flex justify-content-between">
                            <b-button 
                                size="sm" 
                                variant="outline-secondary"
                                @click="showAddCardForDetails = !showAddCardForDetails"
                            >
                                <i class="fas fa-plus mr-1"></i>
                                Add New Card
                            </b-button>
                            
                            <b-button 
                                size="sm" 
                                variant="primary"
                                @click="updateSubscriptionPaymentMethod"
                                :disabled="!selectedCardForDetails || updatingPaymentMethod"
                                v-if="selectedCardForDetails"
                            >
                                <b-spinner v-if="updatingPaymentMethod" size="sm" class="mr-2" />
                                {{ updatingPaymentMethod ? 'Updating...' : 'Update Payment Method' }}
                            </b-button>
                        </div>
                        
                        <!-- Add New Card for Details -->
                        <b-collapse v-model="showAddCardForDetails" class="mt-3">
                            <div class="border-top pt-3">
                                <h6>Add New Payment Method</h6>
                                <div class="mb-3">
                                    <stripe-element-card 
                                        ref="stripeElementCardDetails" 
                                        :pk="stripePublicKey" 
                                        @token="addPaymentMethodForDetails"
                                        v-if="stripePublicKey"
                                    />
                                </div>
                                <div class="d-flex justify-content-end">
                                    <b-button 
                                        size="sm" 
                                        variant="primary"
                                        @click="submitNewCardForDetails"
                                        :disabled="addingCardForDetails"
                                    >
                                        <b-spinner v-if="addingCardForDetails" size="sm" class="mr-2" />
                                        {{ addingCardForDetails ? 'Adding...' : 'Add Card' }}
                                    </b-button>
                                </div>
                            </div>
                        </b-collapse>
                    </b-card>
                </b-collapse>
            </div>
            
            <template #modal-footer>
                <b-button variant="secondary" @click="$bvModal.hide('subscriptionDetailsModal')">
                    Close
                </b-button>
            </template>
        </b-modal>

        <!-- Add Subscription Modal -->
        <b-modal 
            id="addSubscriptionModal" 
            ref="addSubscriptionModal"
            title="Create New Subscription" 
            size="lg"
            @ok="createSubscription"
            @hidden="resetAddSubscriptionModal"
            ok-title="Create Subscription"
            cancel-title="Cancel"
            :ok-disabled="!selectedPlan || !selectedCard"
        >
            <div class="mb-4">
                <h6>Select Plan</h6>
                <b-form-group>
                    <b-form-select v-model="selectedPlan" :options="availablePlans" size="sm">
                        <template #first>
                            <b-form-select-option :value="null" disabled>-- Select a Plan --</b-form-select-option>
                        </template>
                    </b-form-select>
                </b-form-group>
            </div>

            <div class="mb-4">
                <div class="d-flex justify-content-between align-items-center mb-2">
                    <h6 class="mb-0">Select Payment Method</h6>
                    <b-button 
                        size="sm" 
                        variant="success"
                        @click="showAddCardForm = !showAddCardForm"
                        class="add-card-button"
                    >
                        <i class="fas fa-plus mr-1"></i>
                        {{ showAddCardForm ? 'Cancel' : 'Add New Card' }}
                    </b-button>
                </div>

                <div v-if="loadingCards" class="text-center py-2">
                    <b-spinner size="sm" />
                    <span class="ml-2">Loading cards...</span>
                </div>

                <div v-else-if="availableCards.length > 0" class="mb-3">
                    <div v-for="card in availableCards" :key="card.id" class="mb-2">
                        <div class="card-selection" @click="selectedCard = card.id">
                            <div class="d-flex justify-content-between align-items-center">
                                <div class="d-flex align-items-center">
                                    <b-form-radio 
                                        v-model="selectedCard" 
                                        :value="card.id"
                                        class="mr-2"
                                    />
                                    <span>
                                        <i class="fas fa-credit-card mr-2"></i>
                                        {{ card.brand?.toUpperCase() }} ending in {{ card.cardNumber }}
                                        <span class="text-muted ml-2">{{ formatCardExpiration(card.expMonth, card.expYear) }}</span>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-else class="text-muted text-center py-3">
                    <i class="fas fa-credit-card fa-2x mb-2"></i>
                    <p class="mb-0">No cards on file. Click "Add New Card" above to add a payment method.</p>
                </div>

                <!-- Add New Card Form -->
                <b-collapse v-model="showAddCardForm" class="mt-3">
                    <b-card>
                        <h6>Add New Payment Method</h6>
                        <div class="mb-3">
                            <!-- Debug Info -->
                            <div class="mb-2 small text-muted">
                                Debug: Stripe Key Available: {{ !!stripePublicKey }}
                            </div>
                            
                            <div v-if="!stripePublicKey" class="alert alert-warning">
                                <strong>Configuration Issue:</strong> Stripe public key not loaded. Please check your environment configuration.
                                <br><small>Looking for: VITE_STRIPE_PUBLIC_KEY</small>
                            </div>
                            
                            <stripe-element-card 
                                ref="stripeElementCard" 
                                :pk="stripePublicKey" 
                                @token="addPaymentMethod"
                                v-if="stripePublicKey"
                            />
                        </div>
                        <div class="d-flex justify-content-end">
                            <b-button 
                                size="sm" 
                                variant="primary"
                                @click="submitNewCard"
                                :disabled="addingCard"
                            >
                                <b-spinner v-if="addingCard" size="sm" class="mr-2" />
                                {{ addingCard ? 'Adding...' : 'Add Card' }}
                            </b-button>
                        </div>
                    </b-card>
                </b-collapse>
            </div>
        </b-modal>
    </card>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch, Ref } from 'vue-property-decorator';
import { StripeSubscription, StripeSubscriptionModel, StripeSubscriptionStatus } from '@/models/StripeSubscription';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import axios from 'axios';
import moment from 'moment';
import Card from '@/components/Cards/Card.vue';
import { StripeElementCard } from '@vue-stripe/vue-stripe';

@Component({
    name: 'PatientStripeSubscriptionsCard',
    components: {
        Card,
        StripeElementCard
    }
})
export default class PatientStripeSubscriptionsCard extends Vue {
    @Prop({ required: true }) patientId!: number;
    @Ref('stripeElementCard') stripeElementCard!: StripeElementCard;
    @Ref('stripeElementCardDetails') stripeElementCardDetails!: StripeElementCard;

    private subscriptions: StripeSubscriptionModel[] = [];
    private loading = false;
    private showInactive = false;
    private selectedSubscription: StripeSubscriptionModel | null = null;
    
    // Add subscription modal data
    private availablePlans: any[] = [];
    private availableCards: any[] = [];
    private selectedPlan: string | null = null;
    private selectedCard: string | null = null;
    private loadingCards = false;
    private showAddCardForm = false;
    private addingCard = false;
    private stripePublicKey!: string;
    
    // Details modal data
    private currentPaymentMethod: any = null;
    private showChangePaymentMethod = false;
    private availableCardsForDetails: any[] = [];
    private selectedCardForDetails: string | null = null;
    private loadingCardsForDetails = false;
    private showAddCardForDetails = false;
    private addingCardForDetails = false;
    private updatingPaymentMethod = false;

    get filteredSubscriptions(): StripeSubscriptionModel[] {
        if (this.showInactive) {
            return this.subscriptions;
        }
        return this.subscriptions.filter(sub => sub.isActive);
    }

    get inactiveCount(): number {
        return this.subscriptions.filter(sub => sub.isInactive).length;
    }
    
    get hasActiveSubscription(): boolean {
        return this.subscriptions.some(sub => sub.isActive);
    }
    

    @Watch('patientId')
    onPatientIdChanged(newPatientId: number) {
        if (newPatientId) {
            this.loadSubscriptions();
        } else {
            this.subscriptions = [];
        }
    }

    // No need to watch showInactive since we do client-side filtering

    beforeCreate() {
        // Try different ways to access the Stripe public key
        this.stripePublicKey = (this as any)?.$config?.VITE_STRIPE_PUBLIC_KEY || 
                               process.env.VITE_STRIPE_PUBLIC_KEY ||
                               (window as any)?.STRIPE_PUBLIC_KEY;
    }

    mounted() {
        if (this.patientId) {
            this.loadSubscriptions();
        }
    }

    async loadSubscriptions() {
        if (!this.patientId) return;

        this.loading = true;
        try {
            // Always load ALL subscriptions (including inactive ones) for client-side filtering
            const response = await axios.get(`/api/Stripe/subscriptions/${this.patientId}`, { 
                params: { includeInactive: true } 
            });
            
            // Ensure response.data is an array and filter out any null/undefined items
            const subscriptionData = Array.isArray(response.data) ? response.data : [];
            this.subscriptions = subscriptionData
                .filter((sub: StripeSubscription) => sub && sub.id)
                .map((sub: StripeSubscription) => new StripeSubscriptionModel(sub));
        } catch (error) {
            console.error('Error loading Stripe subscriptions:', error);
            this.$notification(NotificationOptions.error('Failed to load Stripe subscriptions'));
        } finally {
            this.loading = false;
        }
    }

    getSubscriptionClasses(subscription: StripeSubscriptionModel): Record<string, boolean> {
        return {
            'subscription-active': subscription.isActive && !subscription.cancelAtPeriodEnd,
            'subscription-cancel-pending': subscription.isActive && subscription.cancelAtPeriodEnd,
            'subscription-inactive': subscription.isInactive,
            'subscription-canceled': subscription.status === StripeSubscriptionStatus.Canceled,
            'subscription-past-due': subscription.status === StripeSubscriptionStatus.PastDue
        };
    }

    getStatusVariant(status: StripeSubscriptionStatus): string {
        switch (status) {
            case StripeSubscriptionStatus.Active:
                return 'success';
            case StripeSubscriptionStatus.Trialing:
                return 'info';
            case StripeSubscriptionStatus.PastDue:
                return 'warning';
            case StripeSubscriptionStatus.Canceled:
            case StripeSubscriptionStatus.Unpaid:
            case StripeSubscriptionStatus.Incomplete:
            case StripeSubscriptionStatus.IncompleteExpired:
                return 'danger';
            default:
                return 'secondary';
        }
    }

    async viewSubscriptionDetails(subscription: StripeSubscriptionModel) {
        this.selectedSubscription = subscription;
        await this.loadCurrentPaymentMethod();
        this.$bvModal.show('subscriptionDetailsModal');
    }

    async cancelSubscription(subscription: StripeSubscriptionModel) {
        const confirmed = await this.$bvModal.msgBoxConfirm(
            `Are you sure you want to cancel the subscription "${subscription.name}"? The subscription will remain active until the end of the current billing period (${this.formatDate(subscription.nextDueDate)}).`,
            {
                title: 'Confirm Cancellation',
                size: 'md',
                buttonSize: 'sm',
                okVariant: 'danger',
                okTitle: 'Cancel at Period End',
                cancelTitle: 'Keep Subscription',
                footerClass: 'p-2',
                hideHeaderClose: false,
                centered: true
            }
        );

        if (!confirmed) return;

        try {
            await axios.post(`/api/Stripe/subscriptions/${subscription.id}/cancel`);
            this.$notification(NotificationOptions.successSaveNotificationPreset('Subscription scheduled for cancellation at period end'));
            await this.loadSubscriptions();
        } catch (error) {
            console.error('Error canceling subscription:', error);
            this.$notification(NotificationOptions.error('Failed to cancel subscription'));
        }
    }

    formatDate(date: Date | string | undefined): string {
        if (!date) return 'N/A';
        return moment(date).format('MMM DD, YYYY');
    }

    formatDateTime(date: Date | string | undefined): string {
        if (!date) return 'N/A';
        return moment(date).format('MMM DD, YYYY [at] h:mm A');
    }

    // Add subscription functionality
    async showAddSubscriptionModal() {
        console.log('Opening add subscription modal');
        console.log('Stripe public key available:', !!this.stripePublicKey);
        await this.loadAvailablePlans();
        await this.loadAvailableCards();
        console.log('Available cards loaded:', this.availableCards.length);
        this.$bvModal.show('addSubscriptionModal');
    }

    async loadAvailablePlans() {
        try {
            const response = await axios.get('/api/stripe/products');
            this.availablePlans = response.data;
        } catch (error) {
            console.error('Error loading available plans:', error);
            this.$notification(NotificationOptions.error('Failed to load available plans'));
        }
    }

    async loadAvailableCards() {
        if (!this.patientId) return;

        this.loadingCards = true;
        try {
            // First, get patient's Stripe customer ID - this might need to be passed as a prop or retrieved
            const customerResponse = await axios.get(`/api/Patient/${this.patientId}`);
            const stripeCustomerId = customerResponse.data.stripeCustomerId;
            
            if (stripeCustomerId) {
                const response = await axios.get(`/api/stripe/cards/${stripeCustomerId}`);
                this.availableCards = response.data.filter((card: any) => !card.pendingDelete);
            }
        } catch (error) {
            console.error('Error loading available cards:', error);
            this.$notification(NotificationOptions.error('Failed to load payment methods'));
        } finally {
            this.loadingCards = false;
        }
    }

    submitNewCard() {
        if (this.stripeElementCard) {
            (this.stripeElementCard as any).submit();
        }
    }

    async addPaymentMethod(token: any) {
        this.addingCard = true;
        
        try {
            console.log("Stripe Token: ", token);
            const cardToken = token?.id;
            if (!cardToken) {
                this.$notification(NotificationOptions.error('Error getting token from stripe'));
                return;
            }

            // Get patient data for customer info
            const patientResponse = await axios.get(`/api/Patient/${this.patientId}`);
            const patient = patientResponse.data;

            const customerInfo = {
                patientId: this.patientId,
                name: patient.lastName + ', ' + patient.firstName,
                id: patient.stripeCustomerId
            };

            const response = await axios.post(`/api/stripe/${cardToken}`, customerInfo);
            
            if (response.data?.customerId) {
                this.$notification(NotificationOptions.successSaveNotificationPreset('Card'));
                
                // Reload cards and close form
                await this.loadAvailableCards();
                this.showAddCardForm = false;
            }
        } catch (error) {
            console.error('Error adding new card:', error);
            this.$notification(NotificationOptions.error('Failed to add payment method'));
        } finally {
            this.addingCard = false;
        }
    }

    async createSubscription() {
        if (!this.selectedPlan || !this.selectedCard) return;

        try {
            const subscriptionData = {
                planId: this.selectedPlan,
                cardId: this.selectedCard,
                patientId: this.patientId
            };

            await axios.post('/api/stripe/subscriptions', subscriptionData);
            
            this.$notification(NotificationOptions.successSaveNotificationPreset('Subscription created successfully'));
            
            // Reload subscriptions and close modal
            await this.loadSubscriptions();
            this.$bvModal.hide('addSubscriptionModal');

        } catch (error) {
            console.error('Error creating subscription:', error);
            this.$notification(NotificationOptions.error('Failed to create subscription'));
        }
    }

    resetAddSubscriptionModal() {
        this.selectedPlan = null;
        this.selectedCard = null;
        this.showAddCardForm = false;
        this.availablePlans = [];
        this.availableCards = [];
        this.addingCard = false;
    }

    formatCardExpiration(month: number, year: number): string {
        if (!month || !year) return '';
        const monthStr = month.toString().padStart(2, '0');
        const yearStr = year.toString().slice(-2);
        return `${monthStr}/${yearStr}`;
    }

    // Details modal methods
    async loadCurrentPaymentMethod() {
        if (!this.selectedSubscription) return;
        
        try {
            // Get the subscription's default payment method
            const response = await axios.get(`/api/stripe/subscriptions/details/${this.selectedSubscription.id}`);
            const subscriptionDetails = response.data;
            
            if (subscriptionDetails.defaultPaymentMethodId) {
                // Get payment method details
                const cardsResponse = await axios.get(`/api/Patient/${this.patientId}`);
                const patient = cardsResponse.data;
                
                if (patient.stripeCustomerId) {
                    const allCardsResponse = await axios.get(`/api/stripe/cards/${patient.stripeCustomerId}`);
                    this.currentPaymentMethod = allCardsResponse.data.find((card: any) => card.id === subscriptionDetails.defaultPaymentMethodId);
                }
            }
        } catch (error) {
            console.error('Error loading current payment method:', error);
        }
    }

    async loadCardsForDetails() {
        if (!this.patientId) return;

        this.loadingCardsForDetails = true;
        try {
            const customerResponse = await axios.get(`/api/Patient/${this.patientId}`);
            const stripeCustomerId = customerResponse.data.stripeCustomerId;
            
            if (stripeCustomerId) {
                const response = await axios.get(`/api/stripe/cards/${stripeCustomerId}`);
                this.availableCardsForDetails = response.data.filter((card: any) => !card.pendingDelete);
            }
        } catch (error) {
            console.error('Error loading available cards for details:', error);
            this.$notification(NotificationOptions.error('Failed to load payment methods'));
        } finally {
            this.loadingCardsForDetails = false;
        }
    }

    submitNewCardForDetails() {
        if (this.stripeElementCardDetails) {
            (this.stripeElementCardDetails as any).submit();
        }
    }

    async addPaymentMethodForDetails(token: any) {
        this.addingCardForDetails = true;
        
        try {
            const cardToken = token?.id;
            if (!cardToken) {
                this.$notification(NotificationOptions.error('Error getting token from stripe'));
                return;
            }

            const patientResponse = await axios.get(`/api/Patient/${this.patientId}`);
            const patient = patientResponse.data;

            const customerInfo = {
                patientId: this.patientId,
                name: patient.lastName + ', ' + patient.firstName,
                id: patient.stripeCustomerId
            };

            const response = await axios.post(`/api/stripe/${cardToken}`, customerInfo);
            
            if (response.data?.customerId) {
                this.$notification(NotificationOptions.successSaveNotificationPreset('Card'));
                
                await this.loadCardsForDetails();
                this.showAddCardForDetails = false;
            }
        } catch (error) {
            console.error('Error adding new card for details:', error);
            this.$notification(NotificationOptions.error('Failed to add payment method'));
        } finally {
            this.addingCardForDetails = false;
        }
    }

    async updateSubscriptionPaymentMethod() {
        if (!this.selectedSubscription || !this.selectedCardForDetails) return;

        this.updatingPaymentMethod = true;
        try {
            const updateData = {
                subscriptionId: this.selectedSubscription.id,
                paymentMethodId: this.selectedCardForDetails
            };

            await axios.put('/api/stripe/subscriptions/payment-method', updateData);
            
            this.$notification(NotificationOptions.successSaveNotificationPreset('Payment method updated successfully'));
            
            // Reload current payment method and close form
            await this.loadCurrentPaymentMethod();
            this.showChangePaymentMethod = false;
            this.selectedCardForDetails = null;
            
        } catch (error) {
            console.error('Error updating payment method:', error);
            this.$notification(NotificationOptions.error('Failed to update payment method'));
        } finally {
            this.updatingPaymentMethod = false;
        }
    }

    async toggleChangePaymentMethod() {
        this.showChangePaymentMethod = !this.showChangePaymentMethod;
        if (this.showChangePaymentMethod) {
            await this.loadCardsForDetails();
        }
    }

    resetDetailsModal() {
        this.currentPaymentMethod = null;
        this.showChangePaymentMethod = false;
        this.availableCardsForDetails = [];
        this.selectedCardForDetails = null;
        this.showAddCardForDetails = false;
        this.addingCardForDetails = false;
        this.updatingPaymentMethod = false;
    }
}
</script>

<style scoped>
.subscription-item {
    transition: all 0.2s ease-in-out;
}

.subscription-item:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.subscription-active {
    border-left: 4px solid #28a745;
}

.subscription-inactive {
    border-left: 4px solid #6c757d;
}

.subscription-canceled {
    border-left: 4px solid #dc3545;
}

.subscription-past-due {
    border-left: 4px solid #ffc107;
}

.subscription-cancel-pending {
    border-left: 4px solid #fd7e14;
    background-color: #fff3cd;
}

.status-badge {
    font-size: 0.75rem;
    padding: 0.25rem 0.5rem;
}

.subscription-details small {
    line-height: 1.4;
}

.font-monospace {
    font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
    font-size: 0.9em;
}

.action-icons {
    display: flex;
    flex-direction: row;
    gap: 4px;
    justify-content: flex-end;
}

.action-icon {
    width: 28px;
    height: 28px;
    border-radius: 4px;
    color: #6c757d;
    transition: color 0.2s ease;
}

.action-icon:hover {
    color: #495057;
    background-color: rgba(0, 0, 0, 0.05);
}

.action-icon.text-danger:hover {
    color: #dc3545 !important;
    background-color: rgba(220, 53, 69, 0.1);
}


.card-selection {
    padding: 8px;
    border: 1px solid #e9ecef;
    border-radius: 4px;
    transition: border-color 0.2s;
}

.card-selection:hover {
    border-color: #007bff;
}

.add-card-button {
    font-weight: 600;
}
</style>